import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import MembersSidebar from '../components/MembersSidebar';

const MembersPaymentMethods = () => {
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(false);

  useEffect(() => {
    const memberData = localStorage.getItem('member_data');
    if (memberData) {
      const parsedMember = JSON.parse(memberData);
      setMember(parsedMember);
    }
    
    // Fetch payment methods from localStorage (same as addresses)
    const savedPaymentMethods = localStorage.getItem('member_payment_methods');
    if (savedPaymentMethods) {
      setPaymentMethods(JSON.parse(savedPaymentMethods));
    } else {
      setPaymentMethods([]); // Set empty array if none found
    }
    
    setLoading(false);
  }, []);

  const handleLogout = async () => {
    setLogoutLoading(true);
    
    try {
      const token = localStorage.getItem('member_token');
      if (token) {
        await axios.post('https://backend.dlapakistan.com/api/members/logout/', {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.log('Logout API error:', error);
    }
    
    localStorage.removeItem('member_token');
    localStorage.removeItem('member_data');
    
    if (axios.defaults.headers.common['Authorization']) {
      delete axios.defaults.headers.common['Authorization'];
    }
    
    Swal.fire({
      icon: 'success',
      title: 'Signed Out',
      text: 'You have been signed out successfully.',
      timer: 2000,
      showConfirmButton: false,
    });
    
    navigate('/members/members-area');
    setLogoutLoading(false);
  };

  const handleRemovePaymentMethod = (paymentId) => {
    const updatedMethods = paymentMethods.filter(method => method.id !== paymentId);
    setPaymentMethods(updatedMethods);
    localStorage.setItem('member_payment_methods', JSON.stringify(updatedMethods));
  };

  const handleSetDefault = (paymentId) => {
    const updatedMethods = paymentMethods.map(method => ({
      ...method,
      isDefault: method.id === paymentId
    }));
    setPaymentMethods(updatedMethods);
    localStorage.setItem('member_payment_methods', JSON.stringify(updatedMethods));
  };

  const handleAddPaymentMethod = () => {
    navigate('/dlashop/checkout'); // Go to checkout to add payment method
  };

  if (!member) {
    return (
      <Container fluid className="py-5 min-vh-100 mt-40" style={{ maxWidth: '95%' }}>
        <div className="text-center py-5">
          <p>Loading...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-5 min-vh-100 mt-40" style={{ maxWidth: '95%' }}>
      {/* Header with user info and logout - EXACTLY like other pages */}
      <div className="mb-6">
        <h1 className="display-6 text-dark mb-3">
          Hello {member.first_name} {member.last_name}
        </h1>
        <p className="text-muted mb-3">
          Manage Account Details!{' '}{' '}{' '}
          <Button 
            variant="link" 
            onClick={handleLogout}
            disabled={logoutLoading}
            className="text-danger p-0"
            style={{ textDecoration: 'none' }}
          >
            {logoutLoading ? 'Signing out...' : 'Log out'}
          </Button>
        </p>
      </div>

      <Row className="g-4">
        {/* Sidebar Column - SAME as other pages */}
        <Col lg={3} md={4}>
          <MembersSidebar />
        </Col>

        {/* Content Column - SAME as other pages */}
        <Col lg={9} md={8}>
          <div className="bg-white shadow-sm rounded-lg p-4 p-lg-5">
            <div className="text-left">
              <h4 className="text-[#70B444] mb-4">Payment Methods</h4>
              
              {loading ? (
                <div className="text-center py-4">
                  <p>Loading payment methods...</p>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <p className="text-gray-700 mb-0">
                      Manage your saved payment methods below. Payment methods added during checkout will appear here.
                    </p>
                  </div>
                  
                  {/* Payment Methods List */}
                  {paymentMethods.length === 0 ? (
                    // Display when no payment methods exist - SAME as other pages
                    <div className="text-center py-5">
                      <h5 className="text-muted mb-3">No payment methods saved yet.</h5>
                      <p className="text-muted mb-4">
                        You haven't saved any payment methods yet. Add a payment method during checkout.
                      </p>
                      <Button 
                        variant="success" 
                        onClick={handleAddPaymentMethod}
                        className="px-4"
                      >
                        Go to Checkout
                      </Button>
                    </div>
                  ) : (
                    // Display when payment methods exist
                    <>
                      <Row className="g-4 mb-4">
                        {paymentMethods.map((method) => (
                          <Col md={6} key={method.id}>
                            <Card className="h-100 border">
                              <Card.Body>
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                  <div>
                                    <h5 className="mb-1">
                                      {method.cardType || 'Credit Card'}
                                      {method.isDefault && (
                                        <span className="badge bg-success ms-2">Default</span>
                                      )}
                                    </h5>
                                    <small className="text-muted">
                                      Added: {new Date(method.addedDate || Date.now()).toLocaleDateString()}
                                    </small>
                                  </div>
                                  <Button
                                    variant="link"
                                    onClick={() => handleRemovePaymentMethod(method.id)}
                                    className="text-danger p-0"
                                    style={{ textDecoration: 'none' }}
                                  >
                                    ❌
                                  </Button>
                                </div>
                                
                                <div className="payment-details">
                                  <p className="mb-2">
                                    <strong>Card Number:</strong> **** **** **** {method.lastFour || '••••'}
                                  </p>
                                  {method.cardHolder && (
                                    <p className="mb-2">
                                      <strong>Card Holder:</strong> {method.cardHolder}
                                    </p>
                                  )}
                                  {method.expiryDate && (
                                    <p className="mb-0">
                                      <strong>Expires:</strong> {method.expiryDate}
                                    </p>
                                  )}
                                </div>
                                
                                <div className="d-flex justify-content-between mt-3 pt-3 border-top">
                                  <small className="text-muted">
                                    {method.type === 'card' ? 'Credit/Debit Card' : method.type || 'Payment Method'}
                                  </small>
                                  {!method.isDefault && (
                                    <Button
                                      variant="outline-success"
                                      size="sm"
                                      onClick={() => handleSetDefault(method.id)}
                                    >
                                      Set as Default
                                    </Button>
                                  )}
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                      
                      <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                        <Button 
                          variant="outline-secondary" 
                          onClick={() => navigate('/members-home')}
                          className="px-4"
                        >
                          Back to Dashboard
                        </Button>
                        <Button 
                          variant="success" 
                          onClick={handleAddPaymentMethod}
                          className="px-4"
                        >
                          Add Payment Method at Checkout
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )}
              
              <p className="text-muted mb-0 mt-4">
                Select an option from the sidebar to manage other account settings.
              </p>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default MembersPaymentMethods;